import React, { useState, useEffect } from 'react';
import { ShieldCheck, Loader2, AlertTriangle, Copy, Check, X } from 'lucide-react';
import { enrollTotp, challengeAndVerify, enableMfaForUser, updateCrossPanelMfa } from '../lib/mfaService';

interface MFAEnrollProps {
  userId: string;
  onEnrolled: () => void;
  onCancelled: () => void;
}

export function MFAEnroll({ userId, onEnrolled, onCancelled }: MFAEnrollProps) {
  const [step, setStep] = useState<'loading' | 'scan' | 'verify' | 'success'>('loading');
  const [factorId, setFactorId] = useState('');
  const [qrCode, setQrCode] = useState('');
  const [secret, setSecret] = useState('');
  const [code, setCode] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const enrollment = await enrollTotp('SMS Panel Authenticator');
        setFactorId(enrollment.factorId);
        setQrCode(enrollment.qrCode);
        setSecret(enrollment.secret);
        setStep('scan');
      } catch (err: unknown) {
        console.error('Enrollment error:', err);
        setError(err instanceof Error ? err.message : 'Failed to start enrollment');
        setStep('scan');
      }
    })();
  }, []);

  const handleCopySecret = async () => {
    try {
      await navigator.clipboard.writeText(secret);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // fallback
      const textarea = document.createElement('textarea');
      textarea.value = secret;
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand('copy');
      document.body.removeChild(textarea);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleVerify = async (e: React.FormEvent) => {
    e.preventDefault();
    if (code.length !== 6) return;

    setIsLoading(true);
    setError('');

    try {
      await challengeAndVerify(factorId, code);

      // Update the custom MFA tables
      try {
        await enableMfaForUser(userId);
        await updateCrossPanelMfa(userId, true);
      } catch (err) {
        console.error('Failed to update MFA tables:', err);
        // Don't block — the Supabase Auth MFA is the source of truth
      }

      setStep('success');
      setTimeout(() => onEnrolled(), 1500);
    } catch (err: unknown) {
      console.error('Verification error:', err);
      const message = err instanceof Error ? err.message : 'Verification failed';
      if (message.includes('Invalid TOTP') || message.includes('invalid')) {
        setError('Invalid code. Please make sure you scanned the QR code and entered the current code.');
      } else {
        setError(message);
      }
      setCode('');
    } finally {
      setIsLoading(false);
    }
  };

  if (step === 'loading') {
    return (
      <div className="flex flex-col items-center justify-center py-12 space-y-4">
        <Loader2 className="w-8 h-8 animate-spin text-green-600" />
        <p className="text-sm text-gray-600">Setting up 2FA...</p>
      </div>
    );
  }

  if (step === 'success') {
    return (
      <div className="flex flex-col items-center justify-center py-12 space-y-4">
        <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center">
          <ShieldCheck className="w-9 h-9 text-green-600" />
        </div>
        <h3 className="text-lg font-semibold text-gray-900">2FA Enabled Successfully!</h3>
        <p className="text-sm text-gray-600">Your account is now protected with two-factor authentication.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-gray-900">Set Up Two-Factor Authentication</h3>
        <button
          onClick={onCancelled}
          className="p-1 hover:bg-gray-100 rounded-full transition-colors"
        >
          <X className="w-5 h-5 text-gray-500" />
        </button>
      </div>

      {/* Error */}
      {error && (
        <div className="rounded-md bg-red-50 border border-red-200 p-3">
          <div className="flex items-start">
            <AlertTriangle className="w-4 h-4 text-red-600 mt-0.5 mr-2 flex-shrink-0" />
            <p className="text-sm text-red-700">{error}</p>
          </div>
        </div>
      )}

      {/* Step 1: Scan QR Code */}
      {step === 'scan' && qrCode && (
        <>
          <div className="text-sm text-gray-600 space-y-2">
            <p><strong>Step 1:</strong> Scan this QR code with your authenticator app (Google Authenticator, Authy, 1Password, etc.)</p>
          </div>

          {/* QR Code */}
          <div className="flex justify-center">
            <div className="bg-white p-4 rounded-lg border-2 border-gray-200">
              <img src={qrCode} alt="QR Code for 2FA" className="w-48 h-48" />
            </div>
          </div>

          {/* Manual Secret */}
          <div className="bg-gray-50 rounded-lg p-3">
            <p className="text-xs text-gray-500 mb-1">Can't scan? Enter this code manually:</p>
            <div className="flex items-center gap-2">
              <code className="flex-1 text-sm font-mono bg-white px-3 py-1.5 rounded border border-gray-200 break-all">
                {secret}
              </code>
              <button
                onClick={handleCopySecret}
                className="p-2 hover:bg-gray-200 rounded-md transition-colors flex-shrink-0"
                title="Copy secret"
              >
                {copied ? <Check className="w-4 h-4 text-green-600" /> : <Copy className="w-4 h-4 text-gray-500" />}
              </button>
            </div>
          </div>

          <button
            onClick={() => setStep('verify')}
            className="w-full py-2 px-4 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors text-sm font-medium"
          >
            I've scanned the QR code — Next
          </button>
        </>
      )}

      {/* Step 2: Verify Code */}
      {step === 'verify' && (
        <form onSubmit={handleVerify} className="space-y-4">
          <div className="text-sm text-gray-600">
            <p><strong>Step 2:</strong> Enter the 6-digit code from your authenticator app to verify setup.</p>
          </div>

          <input
            type="text"
            inputMode="numeric"
            autoComplete="one-time-code"
            value={code}
            onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
            placeholder="000000"
            maxLength={6}
            autoFocus
            className="block w-full px-4 py-3 text-center text-2xl font-mono tracking-[0.5em] border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500"
          />

          <div className="flex gap-3">
            <button
              type="button"
              onClick={() => setStep('scan')}
              className="flex-1 py-2 px-4 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 transition-colors text-sm font-medium"
            >
              Back
            </button>
            <button
              type="submit"
              disabled={isLoading || code.length !== 6}
              className="flex-1 py-2 px-4 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
            >
              {isLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Enable 2FA'}
            </button>
          </div>
        </form>
      )}
    </div>
  );
}
